import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import GhatMap from '../../components/GhatMap';
import { PageHero, Icon, PlaceCard, EmptyState, Button } from '../../components/ui';
import { ghatRoutes } from '../../routes/ghatRoutes';

const ghatLocations = ghatRoutes.map((g) => ({ ...g, id: g.path }));

export default function Ghats() {
  const { t } = useTranslation();
  const [view, setView] = useState('list');
  const [q, setQ] = useState('');

  const needle = q.trim().toLowerCase();
  const results = needle
    ? ghatRoutes.filter((g) => `${g.name} ${t(g.name)} ${g.description}`.toLowerCase().includes(needle))
    : ghatRoutes;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Sacred Ghats of Ayodhya',
    description: 'Explore the sacred ghats of Ayodhya on the banks of the Saryu river.',
    url: 'https://ayodhyadham.info/ghats',
    itemListElement: ghatRoutes.map((g, i) => ({
      '@type': 'ListItem', position: i + 1,
      item: {
        '@type': 'TouristAttraction', name: g.name, description: g.description,
        url: `https://ayodhyadham.info/ghats/${g.path}`,
        geo: { '@type': 'GeoCoordinates', latitude: g.coordinates[0], longitude: g.coordinates[1] },
      },
    })),
  };

  return (
    <>
      <SEO
        title="Ghats of the Saryu"
        description="Explore Ram ki Paidi, Guptar Ghat, Lakshman Ghat and the other sacred ghats on the banks of the Saryu river in Ayodhya."
        canonicalUrl="/ghats"
        keywords="Ayodhya ghats, Saryu river ghats, Ram ki Paidi, Naya Ghat Ayodhya, Guptar Ghat"
        structuredData={schema}
      />
      <PageHero compact crumbs={[{ label: t('Home'), to: '/' }, { label: t('Ghats') }]} title={t('Ghats of the Saryu')} subtitle={t('Sacred banks where Ayodhya meets the river')} />

      <div className="container listing-wrap">
        <div className="listing__toolbar">
          <div className="listing__tools" style={{ marginLeft: 0, width: '100%' }}>
            <label className="search">
              <Icon name="search" size={16} />
              <span className="sr-only">{t('Search ghats')}</span>
              <input type="search" placeholder={t('Search ghats…')} value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <div className="view-toggle" role="group" aria-label={t('View')}>
              <button type="button" className={view === 'list' ? 'is-active' : ''} onClick={() => setView('list')}><Icon name="layout-grid" size={15} />{t('List')}</button>
              <button type="button" className={view === 'map' ? 'is-active' : ''} onClick={() => setView('map')}><Icon name="map" size={15} />{t('Map')}</button>
            </div>
          </div>
        </div>

        <div className="listing listing--no-side">
          <div>
            <div className="results-meta"><span>{t('{{count}} ghats', { count: results.length })}</span></div>

            {view === 'map' ? (
              <div className="map-frame"><GhatMap ghats={ghatLocations} /></div>
            ) : results.length === 0 ? (
              <EmptyState icon="waves" title={t('No ghats found')} body={t('Try a different search term.')} action={<Button variant="outline" onClick={() => setQ('')}>{t('Clear search')}</Button>} />
            ) : (
              <div className="card-grid">
                {results.map((g) => (
                  <PlaceCard
                    key={g.path}
                    image={g.image}
                    title={t(g.name)}
                    subtitle={g.description}
                    to={`/ghats/${g.path}`}
                    meta={[{ icon: 'clock', text: t(g.timings) }, { icon: 'waves', text: t('Saryu river bank') }]}
                    badge={g.tags?.[0] ? t(g.tags[0]) : undefined}
                    cta={t('Explore')}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
